// Rekursiva block-helpers som hanterar både top-level och nestade block
// (inne i twoColumns left/right). All operationer är immutable — varje funktion
// returnerar nya Block[]-arrayer så att React kan diff:a effektivt.

import { Block, BlockId, BlockType, cloneBlock, createBlock, TwoColumnsBlock } from './types';

// Identifierar en plats i block-trädet — antingen direkt under root eller
// inne i en specifik kolumn av ett twoColumns-block.
export type DropTarget = { kind: 'top'; index: number } | { kind: 'column'; parentId: BlockId; column: 'left' | 'right'; index: number };

// Hjälpare: skapar en ny twoColumns med ändrat innehåll i specificerad kolumn.
const replaceColumn = (block: TwoColumnsBlock, column: 'left' | 'right', next: Block[]): TwoColumnsBlock =>
  column === 'left' ? { ...block, left: next } : { ...block, right: next };

// Hittar block med givet id var det än sitter i trädet.
export const findBlockById = (blocks: Block[], id: BlockId): Block | null =>
  blocks.reduce<Block | null>((acc, b) => {
    if (acc) return acc;
    if (b.id === id) return b;
    if (b.type === 'twoColumns') {
      return findBlockById(b.left, id) ?? findBlockById(b.right, id);
    }
    return null;
  }, null);

// Uppdaterar block med matchande id med en partial patch — fungerar både
// på top-level och nestat. Bevarar identiteten på syskon och förfäder så
// React inte unmountar i onödan.
export const updateBlockById = (blocks: Block[], id: BlockId, patch: Partial<Block>): Block[] =>
  blocks.map((b) => {
    if (b.id === id) return { ...b, ...patch } as Block;
    if (b.type === 'twoColumns') {
      return {
        ...b,
        left: updateBlockById(b.left, id, patch),
        right: updateBlockById(b.right, id, patch),
      };
    }
    return b;
  });

// Tar bort block med matchande id var det än sitter. Returnerar både den
// trimmade arrayen och själva blocket som plockades ut (för reorder-flöden
// där samma block ska sättas in på annan plats).
export const removeBlockById = (blocks: Block[], id: BlockId): { blocks: Block[]; removed: Block | null } => {
  const state: { blocks: Block[]; removed: Block | null } = { blocks: [], removed: null };
  blocks.forEach((b) => {
    if (b.id === id) {
      state.removed = b;
      return;
    }
    if (b.type === 'twoColumns') {
      const left = removeBlockById(b.left, id);
      if (left.removed) {
        state.removed = left.removed;
        state.blocks.push({ ...b, left: left.blocks });
        return;
      }
      const right = removeBlockById(b.right, id);
      if (right.removed) {
        state.removed = right.removed;
        state.blocks.push({ ...b, right: right.blocks });
        return;
      }
    }
    state.blocks.push(b);
  });
  return state;
};

// Flyttar block ett steg upp eller ner inom dess förälder (top-level eller
// kolumn). Lämnar trädet orört om flytten skulle gå utanför föräldern.
export const moveBlockById = (blocks: Block[], id: BlockId, direction: 'up' | 'down'): Block[] => {
  const idx = blocks.findIndex((b) => b.id === id);
  if (idx >= 0) {
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= blocks.length) return blocks;
    const next = [...blocks];
    [next[idx], next[target]] = [next[target], next[idx]];
    return next;
  }
  return blocks.map((b) => {
    if (b.type === 'twoColumns') {
      return {
        ...b,
        left: moveBlockById(b.left, id, direction),
        right: moveBlockById(b.right, id, direction),
      };
    }
    return b;
  });
};

// Duplicerar block direkt efter originalet inom dess förälder. Returnerar
// den nya arrayen samt id:t på kopian (så att modal kan markera den).
export const duplicateBlockById = (blocks: Block[], id: BlockId): { blocks: Block[]; newId: BlockId | null } => {
  const idx = blocks.findIndex((b) => b.id === id);
  if (idx >= 0) {
    const copy = cloneBlock(blocks[idx]);
    const next = [...blocks];
    next.splice(idx + 1, 0, copy);
    return { blocks: next, newId: copy.id };
  }
  let newId: BlockId | null = null;
  const nextBlocks = blocks.map((b) => {
    if (newId) return b; // klar — bevara övriga
    if (b.type === 'twoColumns') {
      const l = duplicateBlockById(b.left, id);
      if (l.newId) {
        newId = l.newId;
        return { ...b, left: l.blocks };
      }
      const r = duplicateBlockById(b.right, id);
      if (r.newId) {
        newId = r.newId;
        return { ...b, right: r.blocks };
      }
    }
    return b;
  });
  return { blocks: nextBlocks, newId };
};

// Sätter in ett färdigt block på given drop-target. För kolumn-targets letar
// vi rekursivt efter twoColumns-blocket via dess id och sätter in i rätt
// kolumn på rätt index.
export const insertBlockAtTarget = (blocks: Block[], block: Block, target: DropTarget): Block[] => {
  if (target.kind === 'top') {
    const next = [...blocks];
    const i = Math.max(0, Math.min(target.index, next.length));
    next.splice(i, 0, block);
    return next;
  }
  return blocks.map((b) => {
    if (b.id === target.parentId && b.type === 'twoColumns') {
      const col = target.column === 'left' ? b.left : b.right;
      const next = [...col];
      const i = Math.max(0, Math.min(target.index, next.length));
      next.splice(i, 0, block);
      return replaceColumn(b, target.column, next);
    }
    if (b.type === 'twoColumns') {
      // target.parentId kan ligga djupare — fortsätt rekursivt även om vi
      // praktiskt inte tillåter nested twoColumns idag.
      return {
        ...b,
        left: insertBlockAtTarget(b.left, block, target),
        right: insertBlockAtTarget(b.right, block, target),
      };
    }
    return b;
  });
};

// Bekvämlighetsfunktion: skapa nytt block av given typ och sätt in på target.
export const insertNewBlockAtTarget = (blocks: Block[], type: BlockType, target: DropTarget): { blocks: Block[]; newId: BlockId } => {
  const newBlock = createBlock(type);
  return { blocks: insertBlockAtTarget(blocks, newBlock, target), newId: newBlock.id };
};

// Flyttar ett befintligt block till ett nytt target. Implementeras som
// remove + insert. Om target ligger efter ursprungspositionen i samma
// förälder justerar callern target.index — den här funktionen flyttar bara.
export const reorderBlockToTarget = (blocks: Block[], id: BlockId, target: DropTarget): Block[] => {
  const { blocks: trimmed, removed } = removeBlockById(blocks, id);
  if (!removed) return blocks;
  return insertBlockAtTarget(trimmed, removed, target);
};

// Hittar förälder-information om ett block — används för "kan flytta upp/ner?"-checks
// i toolbar:en (knapp disablas om blocket är först/sist i sin förälder).
export interface BlockParentInfo {
  siblings: Block[];
  index: number;
}

export const findBlockParent = (blocks: Block[], id: BlockId): BlockParentInfo | null => {
  const i = blocks.findIndex((b) => b.id === id);
  if (i >= 0) return { siblings: blocks, index: i };
  return blocks.reduce<BlockParentInfo | null>((acc, b) => {
    if (acc) return acc;
    if (b.type === 'twoColumns') {
      return findBlockParent(b.left, id) ?? findBlockParent(b.right, id);
    }
    return null;
  }, null);
};
